import React, { useState } from 'react';
import { Check, ChevronDown, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { useClickOutside } from '@/hooks/useClickOutside';

interface Option {
  value: string | number;
  label: string;
}

interface MultiSelectProps {
  options: Option[];
  value: (string | number)[];
  onChange: (value: (string | number)[]) => void;
  placeholder?: string;
  className?: string;
  searchable?: boolean;
  disabled?: boolean;
  dropdownPosition?: 'top' | 'bottom';
}

const MultiSelect = ({ 
  options, 
  value, 
  onChange, 
  placeholder = "Select options...", 
  className = "",
  searchable = true,
  disabled = false,
  dropdownPosition = 'bottom'
}: MultiSelectProps) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Handle click outside to close dropdown
  const dropdownRef = useClickOutside<HTMLDivElement>(() => {
    if (open) {
      setOpen(false);
    }
  });

  const filteredOptions = searchable
    ? (options || []).filter(option =>
        option &&
        option.label &&
        typeof option.label === 'string' &&
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : (options || []);

  const handleSelect = (optionValue: string | number) => {
    if (disabled) return;
    
    
    const newValue = value.includes(optionValue)
      ? value.filter(v => v !== optionValue)
      : [...value, optionValue];
      
    // Check for duplicates and remove them as a safety measure
    const uniqueValue = [...new Set(newValue)];
    
    onChange(uniqueValue);
  };

  const handleRemove = (optionValue: string | number) => {
    if (disabled) return;
    
    const newValue = value.filter(v => v !== optionValue);
    
    // Ensure no duplicates
    const uniqueValue = [...new Set(newValue)];
    onChange(uniqueValue);
  };

  const handleButtonClick = () => {
    if (!disabled) {
      setOpen(!open);
    }
  };

  const selectedOptions = (options || []).filter(option =>
    option && option.value !== undefined && value.includes(option.value)
  );

  return (
    <div className={className}>
      <div className="relative" ref={dropdownRef}>
        {/* Custom button instead of PopoverTrigger */}
        <button
          type="button"
          className="flex w-full min-h-[40px] items-start justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          onClick={handleButtonClick}
          disabled={disabled}
        >
          <div className="flex flex-wrap gap-1 flex-1 min-w-0 max-w-full overflow-hidden">
            {selectedOptions.length === 0 ? (
              <span className="text-muted-foreground">{placeholder}</span>
            ) : (
              selectedOptions.map((option) => (
                <Badge
                  key={option.value}
                  variant="secondary"
                  className="flex items-center gap-1 text-xs px-2 py-1 max-w-full"
                >
                  <span className="truncate max-w-[120px]">{option.label}</span>
                  {!disabled && (
                    <X
                      className="h-3 w-3 cursor-pointer hover:text-red-500 transition-colors flex-shrink-0"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleRemove(option.value);
                      }}
                    />
                  )}
                </Badge>
              ))
            )}
          </div>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50 ml-2" />
        </button>

        {/* Custom dropdown */}
        {open && !disabled && (
          <div className={`absolute left-0 right-0 z-50 max-h-60 overflow-auto rounded-md border bg-popover p-1 text-popover-foreground shadow-md ${
            dropdownPosition === 'top' ? 'bottom-full mb-1' : 'top-full mt-1'
          }`}>
            {searchable && (
              <div className="p-2 border-b">
                <Input
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="h-8"
                />
              </div>
            )}
            <div className="p-1">
              {filteredOptions.length === 0 ? (
                <div className="py-2 px-3 text-sm text-muted-foreground">
                  No options found.
                </div>
              ) : (
                filteredOptions.map((option) => (
                  <div
                    key={option.value}
                    className="flex items-center space-x-2 py-2 px-3 cursor-pointer hover:bg-accent rounded-sm"
                    onClick={() => handleSelect(option.value)}
                  >
                    <div className="flex h-4 w-4 items-center justify-center border border-primary rounded-sm">
                      {value.includes(option.value) && (
                        <Check className="h-3 w-3 text-primary" />
                      )}
                    </div>
                    <span className="text-sm">{option.label}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export { MultiSelect };
export default MultiSelect;